import React, { useMemo, useRef } from 'react';
import { EventPageFragment, EventPageType } from '@graphql/generated';
import { BoxProps } from '@withjoy/joykit';
import { LayoutAlohaProps } from '../../LayoutAloha';
import { pageWidgets, PreparedPages } from './Body.constants';
import Page from '../Page';
import { InlineEditor } from '@shared/components';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { SCREENS } from '@shared/types/fontPackScreens';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';

export interface BodyProps
  extends Readonly<{ readonly page: Maybe<EventPageFragment>; readonly applicatorProps?: object; readonly isWelcomePage?: boolean } & LayoutAlohaProps & BoxProps> {}

export const Body: React.FC<BodyProps> = ({ event, eventHandle, page, applicatorProps = {}, isWelcomePage }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  const isCurrentPage = currentPageSlug === page?.pageSlug;

  const onPageEditClick = useEventCallback(() => {
    if (page) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPage',
          inlineEditData: {
            eventHandle,
            pageSlug: page.pageSlug,
            pageType: page.type,
            pageTitle: page.pageTitle,
          },
        },
      });
    }
  });

  const onVisibilityClick = useEventCallback(() => {
    if (page) {
      // send the message to the parent window to open the text editor
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPageSettings',
          inlineEditData: { pageId: page.id, pageType: page.type, pageTitle: page.pageTitle, pageVisibility: page.visibility, isPageContainer: page.isPageContainer },
        },
      });
    }
  });

  const handleEditFontClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { screen: SCREENS.categoryList } },
    });
  });

  const { isTidbitsPage, isStoryPage, marginTop, isAccommodations } = useMemo(() => {
    return {
      isTidbitsPage: page?.type === 'tidbits',
      isStoryPage: page?.type === 'story',
      marginTop: isWelcomePage ? '0' : '4rem',
      isRegistryPage: page?.type === 'registry',
      isAccommodations: page?.pageSlug === 'accommodations',
    };
  }, [page?.pageSlug, page?.type, isWelcomePage]);

  if (!page) {
    return null;
  }

  const { type } = page;
  const PageWidget = pageWidgets[type as PreparedPages];

  const pageProps = (!isTidbitsPage && {
    title: isWelcomePage ? undefined : page.pageTitle,
    subtitle: !isStoryPage ? page.subTitle : undefined,
    graphicAccent: undefined,
    isAccommodations: isAccommodations,
    isWelcomePage,
    marginTop,
    photo: page.photo,
  }) || {
    title: isWelcomePage ? undefined : page.pageTitle,
    marginTop,
  };
  return PageWidget ? (
    <InlineEditor
      actionData={{
        ...(!isCurrentPage && { edit: onPageEditClick }),
        visibility: {
          function: onVisibilityClick,
          visibility: page.visibility,
        },
        font: handleEditFontClick,
      }}
      inset={page.pageSlug === 'accommodations' && !isMobile ? { x: -12, y: -16 } : { x: 4, y: -16 }}
      wrapperType="actionOutside"
      componentName="page"
      elementLabel={page.pageTitle}
      pageName={page.type}
      pageSlug={page.pageSlug}
      stickyOnScroll={true}
      isEligibleForInlineEditing={page?.type !== EventPageType.app}
    >
      <Page
        {...pageProps}
        visibility={page.visibility}
        pageSlug={page.pageSlug}
        ref={pageRef}
        pageId={page.id}
        pageType={page.type}
        isPageContainer={page.isPageContainer}
      >
        <PageWidget
          isAccommodations={isAccommodations}
          isWelcomePage={isWelcomePage}
          event={event}
          eventHandle={eventHandle}
          {...applicatorProps}
          pageId={page.id}
          pageSlug={page.pageSlug}
          pageTitle={pageProps.title}
        />
      </Page>
    </InlineEditor>
  ) : null;
};
